import {
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  Input,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { FiEdit3 } from "react-icons/fi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { editBusinessUnit } from "./EditBusinessUnit.requests";
import { useComponentColors } from "theme";
import { Button, Modal } from "components";

const validationSchema = z.object({
  displayName: z.string().min(3),
});

type FieldValues = z.infer<typeof validationSchema>;

type EditBusinessUnitProps = {
  companyId: number;
  unitName: string;
  unitId: number;
  isOpen: boolean;
  onClose: () => void;
};

const EditBusinessUnit = ({
  companyId,
  unitName,
  unitId,
  isOpen,
  onClose,
}: EditBusinessUnitProps) => {
  const componentColors = useComponentColors();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FieldValues>({
    mode: "onChange",
    resolver: zodResolver(validationSchema),
    defaultValues: {
      displayName: unitName,
    },
  });

  const { mutate, isLoading } = useMutation(editBusinessUnit, {
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({ queryKey: ["businessUnits"] });
    },
  });

  const onSubmit = (values: FieldValues) => {
    mutate({
      ...values,
      companyId,
      unitId,
    });
  };

  return (
    <Modal
      title="Edit Subsidiary"
      body={
        <chakra.form borderTopRadius={4} borderRadius={4}>
          <FormControl
            isInvalid={!!errors?.displayName?.message}
            as={GridItem}
            isRequired
          >
            <FormLabel
              color={componentColors.form.formLabelColor}
              fontSize="sm"
              fontWeight="md"
            >
              Subsidiary Name
            </FormLabel>

            <Input
              {...register("displayName")}
              type="name"
              name="displayName"
              placeholder="Subsidiary Name"
              shadow="sm"
              size="sm"
              w="300px"
              rounded="md"
            />

            <FormErrorMessage>{errors?.displayName?.message}</FormErrorMessage>
          </FormControl>
        </chakra.form>
      }
      isOpen={isOpen}
      onClose={onClose}
      secondaryButton={
        <Button aria-label="Cancel" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryButton={
        <Button
          aria-label="Edit Subsidiary"
          variant="primary"
          leftIcon={<FiEdit3 />}
          onClick={() => {
            handleSubmit(onSubmit)();
          }}
          isLoading={isLoading}
        >
          Edit
        </Button>
      }
    />
  );
};

export { EditBusinessUnit };
